import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Clock, Users, Building2, CalendarDays, TrendingUp, Download, Edit, Trash2, Eye, X, Save, AlertTriangle, Search } from 'lucide-react';
import { format, startOfMonth, endOfMonth, startOfYear, subMonths, parseISO, startOfDay, endOfDay } from 'date-fns';
import { de } from 'date-fns/locale';

const COLORS = ['#009dde', '#34c759', '#ff9500', '#af52de', '#ff3b30', '#5856d6', '#ff2d55', '#007aff', '#30b0c7', '#a2845e'];

const AdminTimeStats = () => {
    const { api } = useAuth();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [filterUser, setFilterUser] = useState('all');
    const [filterCustomer, setFilterCustomer] = useState('all');
    const [timeRange, setTimeRange] = useState('month');
    // Benutzerdefinierter Zeitraum (Kalender): { from: Date, to: Date }
    const [customRange, setCustomRange] = useState({ from: undefined, to: undefined });
    const [rangePickerOpen, setRangePickerOpen] = useState(false);
    const [view, setView] = useState('employee');
    
    // Detail modal state
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [employeeEntries, setEmployeeEntries] = useState([]);
    const [loadingEntries, setLoadingEntries] = useState(false);
    const [editingEntry, setEditingEntry] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [entrySearch, setEntrySearch] = useState('');

    const getDateRange = useCallback(() => {
        const now = new Date();
        let start, end;
        switch (timeRange) {
            case 'week': {
                const d = new Date(now);
                d.setDate(d.getDate() - d.getDay() + 1);
                start = d;
                end = new Date(now);
                break;
            }
            case 'month':
                start = startOfMonth(now);
                end = endOfMonth(now);
                break;
            case 'quarter':
                start = subMonths(startOfMonth(now), 2);
                end = endOfMonth(now);
                break;
            case 'year':
                start = startOfYear(now);
                end = new Date(now);
                break;
            case 'last_month':
                start = startOfMonth(subMonths(now, 1));
                end = endOfMonth(subMonths(now, 1));
                break;
            case 'custom':
                if (customRange.from) {
                    start = startOfDay(customRange.from);
                    end = endOfDay(customRange.to || customRange.from);
                } else {
                    start = startOfMonth(now);
                    end = endOfMonth(now);
                }
                break;
            default:
                start = startOfMonth(now);
                end = endOfMonth(now);
        }
        return {
            start_date: start.toISOString(),
            end_date: end.toISOString()
        };
    }, [timeRange, customRange]);

    const fetchStats = useCallback(async () => {
        setLoading(true);
        try {
            const { start_date, end_date } = getDateRange();
            const params = new URLSearchParams({ start_date, end_date });
            if (filterUser !== 'all') params.append('user_id', filterUser);
            if (filterCustomer !== 'all') params.append('customer_id', filterCustomer);
            
            const res = await api.get(`/admin/time-stats?${params}`);
            setStats(res.data);
        } catch (err) {
            toast.error('Fehler beim Laden der Statistiken');
        } finally {
            setLoading(false);
        }
    }, [api, getDateRange, filterUser, filterCustomer]);

    useEffect(() => {
        const loadFilters = async () => {
            try {
                const [usersRes, customersRes] = await Promise.all([
                    api.get('/users'),
                    api.get('/customers')
                ]);
                setUsers(usersRes.data);
                setCustomers(customersRes.data);
            } catch {}
        };
        loadFilters();
    }, [api]);

    useEffect(() => { fetchStats(); }, [fetchStats]);

    // Load employee entries when selected
    const loadEmployeeEntries = async (employee) => {
        setSelectedEmployee(employee);
        setEntrySearch('');
        setLoadingEntries(true);
        try {
            const { start_date, end_date } = getDateRange();
            const params = new URLSearchParams({ 
                start_date, 
                end_date,
                user_id: employee.user_id
            });
            const res = await api.get(`/time-entries/admin/all?${params}`);
            setEmployeeEntries(res.data || []);
        } catch (err) {
            toast.error('Fehler beim Laden der Einträge');
            setEmployeeEntries([]);
        } finally {
            setLoadingEntries(false);
        }
    };

    // Delete entry
    const handleDeleteEntry = async (entryId) => {
        if (!window.confirm('Eintrag wirklich löschen?')) return;
        try {
            await api.delete(`/time-entries/${entryId}`);
            toast.success('Eintrag gelöscht');
            setEmployeeEntries(prev => prev.filter(e => e.id !== entryId));
            fetchStats();
        } catch (err) {
            toast.error('Fehler beim Löschen');
        }
    };

    // Start editing entry
    const startEditEntry = (entry) => {
        setEditingEntry(entry);
        setEditForm({
            date: entry.date || entry.start_time?.split('T')[0] || '',
            duration_minutes: entry.duration_minutes || Math.round((new Date(entry.end_time) - new Date(entry.start_time)) / 60000),
            activity_type: entry.activity_type || '',
            description: entry.description || '',
            customer_id: entry.customer_id || ''
        });
    };

    // Save edited entry
    const handleSaveEntry = async () => {
        if (!editingEntry) return;
        try {
            // Derive customer_name from customer_id so stats stay in sync
            const selectedCustomer = customers.find(c => c.id === editForm.customer_id);
            const payload = {
                ...editForm,
                customer_id: editForm.customer_id || null,
                customer_name: selectedCustomer?.name || null,
            };
            await api.put(`/time-entries/${editingEntry.id}`, payload);
            toast.success('Eintrag aktualisiert');
            setEditingEntry(null);
            // Reload entries
            if (selectedEmployee) {
                loadEmployeeEntries(selectedEmployee);
            }
            fetchStats();
        } catch (err) {
            toast.error('Fehler beim Speichern');
        }
    };

    const formatHours = (h) => {
        const hours = Math.floor(h);
        const mins = Math.round((h - hours) * 60);
        return `${hours}h ${mins}m`;
    };

    const RANGE_LABELS = {
        week: 'Diese Woche',
        month: 'Dieser Monat',
        last_month: 'Letzter Monat',
        quarter: 'Letztes Quartal',
        year: 'Dieses Jahr',
        custom: 'Benutzerdefiniert'
    };

    const customRangeLabel = customRange.from
        ? `${format(customRange.from, 'dd.MM.yyyy')} – ${format(customRange.to || customRange.from, 'dd.MM.yyyy')}`
        : 'Zeitraum wählen...';

    const exportCSV = async () => {
        if (!stats) return;
        try {
            // Fetch all individual entries for current filter (user, customer, range)
            const { start_date, end_date } = getDateRange();
            const params = new URLSearchParams({ start_date, end_date });
            if (filterUser !== 'all') params.append('user_id', filterUser);
            if (filterCustomer !== 'all') params.append('customer_id', filterCustomer);
            const entriesRes = await api.get(`/time-entries/admin/all?${params}`);
            const entries = entriesRes.data || [];

            // Build user lookup for entry names
            const userMap = users.reduce((acc, u) => {
                acc[u.id] = `${u.first_name || ''} ${u.last_name || ''}`.trim();
                return acc;
            }, {});

            const escapeCsv = (v) => {
                const s = (v ?? '').toString();
                if (/[;"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
                return s;
            };

            const rows = [];

            // Section 1: Detailed Entries
            rows.push(['Datum', 'Mitarbeiter', 'Kunde', 'Aktivität', 'Beschreibung', 'Dauer (Stunden)', 'Dauer (h:m)']);
            const sortedEntries = [...entries].sort((a, b) => {
                const da = a.date || a.start_time || '';
                const db = b.date || b.start_time || '';
                return da.localeCompare(db);
            });
            sortedEntries.forEach(e => {
                const dateStr = e.date
                    ? format(parseISO(e.date), 'dd.MM.yyyy', { locale: de })
                    : (e.start_time ? format(parseISO(e.start_time), 'dd.MM.yyyy', { locale: de }) : '');
                const mins = e.duration_minutes || (e.duration_seconds ? Math.round(e.duration_seconds / 60) : 0);
                const hours = (mins / 60).toFixed(2);
                const hm = `${Math.floor(mins / 60)}h ${mins % 60}m`;
                rows.push([
                    dateStr,
                    userMap[e.user_id] || e.user_id || '',
                    e.customer_name || 'Kein Kunde',
                    e.activity_type || '',
                    e.description || '',
                    hours,
                    hm,
                ]);
            });

            // Section 2: Aggregate by Employee
            rows.push([]);
            rows.push(['--- Zusammenfassung Mitarbeiter ---']);
            rows.push(['Mitarbeiter', 'Stunden', 'Einträge']);
            stats.by_employee.forEach(e => rows.push([e.name, e.total_hours, e.entries_count]));

            // Section 3: Aggregate by Customer
            rows.push([]);
            rows.push(['--- Zusammenfassung Kunden ---']);
            rows.push(['Kunde', 'Stunden', 'Einträge']);
            stats.by_customer.forEach(c => rows.push([c.name, c.total_hours, c.entries_count]));

            // Total
            rows.push([]);
            rows.push(['Gesamt', stats.total_hours, stats.total_entries]);

            const csv = rows.map(r => r.map(escapeCsv).join(';')).join('\n');
            // Add BOM for Excel UTF-8 compatibility
            const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `zeiterfassung-${timeRange}-${format(new Date(), 'yyyy-MM-dd')}.csv`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success(`CSV exportiert (${entries.length} Einträge)`);
        } catch (err) {
            toast.error('Fehler beim CSV-Export');
        }
    };

    return (
        <div className="flex flex-col gap-8" data-testid="admin-time-stats">

            {/* Filters */}
            <Card>
                <CardContent className="pt-6">
                    <div className="flex flex-wrap gap-3 items-end">
                        <div className="flex-1 min-w-[140px]">
                            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Zeitraum</label>
                            <Select value={timeRange} onValueChange={setTimeRange}>
                                <SelectTrigger className="rounded-xl" data-testid="time-range-filter">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.entries(RANGE_LABELS).map(([k, v]) => (
                                        <SelectItem key={k} value={k}>{v}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {timeRange === 'custom' && (
                            <div className="flex-1 min-w-[220px]">
                                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Von – Bis</label>
                                <Popover open={rangePickerOpen} onOpenChange={setRangePickerOpen}>
                                    <PopoverTrigger asChild>
                                        <Button variant="outline" className="w-full justify-start font-normal rounded-xl" data-testid="custom-range-trigger">
                                            <CalendarDays className="mr-2 h-4 w-4" />
                                            <span className={customRange.from ? '' : 'text-muted-foreground'}>{customRangeLabel}</span>
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                        <Calendar
                                            mode="range"
                                            numberOfMonths={2}
                                            selected={customRange}
                                            onSelect={(range) => {
                                                const next = range || { from: undefined, to: undefined };
                                                setCustomRange(next);
                                                if (next.from && next.to) setRangePickerOpen(false);
                                            }}
                                            locale={de}
                                            weekStartsOn={1}
                                        />
                                        <div className="flex justify-between items-center px-3 py-2 border-t border-border text-xs text-muted-foreground">
                                            <span>Erst Start-, dann Enddatum klicken</span>
                                            <button
                                                type="button"
                                                className="hover:text-foreground"
                                                onClick={() => setCustomRange({ from: undefined, to: undefined })}
                                            >
                                                Zurücksetzen
                                            </button>
                                        </div>
                                    </PopoverContent>
                                </Popover>
                            </div>
                        )}
                        <div className="flex-1 min-w-[140px]">
                            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Mitarbeiter</label>
                            <Select value={filterUser} onValueChange={setFilterUser}>
                                <SelectTrigger className="rounded-xl" data-testid="employee-filter">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Alle Mitarbeiter</SelectItem>
                                    {users.map(u => (
                                        <SelectItem key={u.id} value={u.id}>
                                            {u.first_name} {u.last_name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex-1 min-w-[140px]">
                            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Kunde</label>
                            <Select value={filterCustomer} onValueChange={setFilterCustomer}>
                                <SelectTrigger className="rounded-xl" data-testid="customer-filter">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Alle Kunden</SelectItem>
                                    {customers.map(c => (
                                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <Button variant="outline" onClick={exportCSV} className="rounded-xl gap-2" data-testid="export-csv-btn">
                            <Download className="w-4 h-4" />
                            CSV
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Summary Cards */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                    <Card>
                        <CardContent className="pt-5 pb-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                                    <Clock className="w-5 h-5 text-blue-500" />
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">Gesamtstunden</p>
                                    <p className="text-xl font-bold" data-testid="total-hours">{formatHours(stats.total_hours)}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-5 pb-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center">
                                    <TrendingUp className="w-5 h-5 text-green-500" />
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">Einträge</p>
                                    <p className="text-xl font-bold" data-testid="total-entries">{stats.total_entries}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-5 pb-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                                    <Users className="w-5 h-5 text-purple-500" />
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">Mitarbeiter</p>
                                    <p className="text-xl font-bold">{stats.by_employee.length} <span className="text-sm font-normal text-muted-foreground">/ {users.length}</span></p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-5 pb-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center">
                                    <Building2 className="w-5 h-5 text-orange-500" />
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">Kunden</p>
                                    <p className="text-xl font-bold">{stats.by_customer.length} <span className="text-sm font-normal text-muted-foreground">/ {customers.length}</span></p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {loading && (
                <div className="flex items-center justify-center py-12">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
            )}

            {stats && !loading && (
                <>
                    {/* View Tabs */}
                    <div className="flex gap-2">
                        {[
                            { key: 'employee', label: 'Nach Mitarbeiter', icon: Users },
                            { key: 'customer', label: 'Nach Kunde', icon: Building2 },
                            { key: 'timeline', label: 'Zeitverlauf', icon: CalendarDays }
                        ].map(({ key, label, icon: Icon }) => (
                            <Button
                                key={key}
                                variant={view === key ? 'default' : 'outline'}
                                onClick={() => setView(key)}
                                className="rounded-xl gap-2"
                                data-testid={`view-${key}`}
                            >
                                <Icon className="w-4 h-4" />
                                {label}
                            </Button>
                        ))}
                    </div>

                    {/* Employee View */}
                    {view === 'employee' && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <Card>
                                <CardHeader><CardTitle className="text-base">Stunden pro Mitarbeiter</CardTitle></CardHeader>
                                <CardContent>
                                    <div className="h-[300px]">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={stats.by_employee.slice(0, 10)} layout="vertical">
                                                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                                <XAxis type="number" tick={{ fontSize: 12 }} />
                                                <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={100} />
                                                <Tooltip formatter={(v) => [`${formatHours(v)}`, 'Stunden']} />
                                                <Bar dataKey="total_hours" fill="#009dde" radius={[0, 6, 6, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardHeader><CardTitle className="text-base">Mitarbeiter Details</CardTitle></CardHeader>
                                <CardContent>
                                    <div className="space-y-3 max-h-[300px] overflow-y-auto">
                                        {stats.by_employee.map((emp, i) => (
                                            <div 
                                                key={emp.user_id} 
                                                className="flex items-center gap-3 p-2.5 rounded-xl bg-muted/30 hover:bg-muted/50 cursor-pointer transition-colors group"
                                                onClick={() => loadEmployeeEntries(emp)}
                                            >
                                                <Avatar className="w-9 h-9">
                                                    {emp.profile_image ? (
                                                        <AvatarImage src={emp.profile_image} className="object-cover" />
                                                    ) : (
                                                        <AvatarFallback style={{ backgroundColor: emp.profile_color }} className="text-white text-xs">
                                                            {emp.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                                        </AvatarFallback>
                                                    )}
                                                </Avatar>
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-medium text-sm truncate">{emp.name}</p>
                                                    <p className="text-xs text-muted-foreground">{emp.entries_count} Einträge</p>
                                                </div>
                                                <div className="text-right flex items-center gap-2">
                                                    <p className="font-semibold text-sm">{formatHours(emp.total_hours)}</p>
                                                    <Eye className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                                                </div>
                                            </div>
                                        ))}
                                        {stats.by_employee.length === 0 && (
                                            <p className="text-center text-muted-foreground py-8">Keine Daten vorhanden</p>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    )}

                    {/* Customer View */}
                    {view === 'customer' && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <Card>
                                <CardHeader><CardTitle className="text-base">Stunden pro Kunde</CardTitle></CardHeader>
                                <CardContent>
                                    <div className="h-[300px]">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={stats.by_customer.slice(0, 8)}
                                                    dataKey="total_hours"
                                                    nameKey="name"
                                                    cx="50%" cy="50%"
                                                    outerRadius={100}
                                                    label={({ name, total_hours }) => `${name}: ${formatHours(total_hours)}`}
                                                    labelLine={true}
                                                >
                                                    {stats.by_customer.slice(0, 8).map((_, i) => (
                                                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                                                    ))}
                                                </Pie>
                                                <Tooltip formatter={(v) => [`${formatHours(v)}`, 'Stunden']} />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardHeader><CardTitle className="text-base">Kunden Details</CardTitle></CardHeader>
                                <CardContent>
                                    <div className="space-y-3 max-h-[300px] overflow-y-auto">
                                        {stats.by_customer.map((cust, i) => (
                                            <div key={cust.customer_id} className="flex items-center gap-3 p-2.5 rounded-xl bg-muted/30">
                                                <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-medium text-xs"
                                                    style={{ backgroundColor: COLORS[i % COLORS.length] }}>
                                                    {cust.name.substring(0, 2).toUpperCase()}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-medium text-sm truncate">{cust.name}</p>
                                                    <p className="text-xs text-muted-foreground">{cust.entries_count} Einträge</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="font-semibold text-sm">{formatHours(cust.total_hours)}</p>
                                                </div>
                                            </div>
                                        ))}
                                        {stats.by_customer.length === 0 && (
                                            <p className="text-center text-muted-foreground py-8">Keine Daten vorhanden</p>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    )}

                    {/* Timeline View */}
                    {view === 'timeline' && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <Card>
                                <CardHeader><CardTitle className="text-base">Stunden pro Woche</CardTitle></CardHeader>
                                <CardContent>
                                    <div className="h-[300px]">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={stats.by_week}>
                                                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                                <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                                                <YAxis tick={{ fontSize: 12 }} />
                                                <Tooltip formatter={(v) => [`${formatHours(v)}`, 'Stunden']} />
                                                <Bar dataKey="total_hours" fill="#34c759" radius={[6, 6, 0, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardHeader><CardTitle className="text-base">Stunden pro Monat</CardTitle></CardHeader>
                                <CardContent>
                                    <div className="h-[300px]">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={stats.by_month}>
                                                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                                                <YAxis tick={{ fontSize: 12 }} />
                                                <Tooltip formatter={(v) => [`${formatHours(v)}`, 'Stunden']} />
                                                <Bar dataKey="total_hours" fill="#af52de" radius={[6, 6, 0, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    )}
                </>
            )}

            {/* Employee Detail Modal */}
            <Dialog open={!!selectedEmployee} onOpenChange={(open) => !open && setSelectedEmployee(null)}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden p-0">
                    <DialogHeader className="p-6 pb-0">
                        <DialogTitle className="flex items-center gap-3">
                            {selectedEmployee && (
                                <>
                                    <Avatar className="w-10 h-10">
                                        {selectedEmployee.profile_image ? (
                                            <AvatarImage src={selectedEmployee.profile_image} className="object-cover" />
                                        ) : (
                                            <AvatarFallback style={{ backgroundColor: selectedEmployee.profile_color }} className="text-white">
                                                {selectedEmployee.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                            </AvatarFallback>
                                        )}
                                    </Avatar>
                                    <div>
                                        <span>{selectedEmployee.name}</span>
                                        <p className="text-sm font-normal text-muted-foreground">
                                            {selectedEmployee.entries_count} Einträge • {formatHours(selectedEmployee.total_hours)}
                                        </p>
                                    </div>
                                </>
                            )}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <ScrollArea className="max-h-[60vh] px-6 pb-6">
                        {/* Search Filter */}
                        <div className="sticky top-0 bg-background pt-2 pb-3 z-10">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                                <Input
                                    placeholder="Nach Beschreibung, Kunde oder Aktivität suchen..."
                                    value={entrySearch}
                                    onChange={(e) => setEntrySearch(e.target.value)}
                                    className="pl-9 rounded-xl"
                                    data-testid="entry-search-input"
                                />
                                {entrySearch && (
                                    <button
                                        onClick={() => setEntrySearch('')}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                        data-testid="entry-search-clear"
                                        aria-label="Suche zurücksetzen"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                            {entrySearch && (() => {
                                const term = entrySearch.toLowerCase();
                                const filteredCount = employeeEntries.filter(e => 
                                    (e.description || '').toLowerCase().includes(term) ||
                                    (e.customer_name || '').toLowerCase().includes(term) ||
                                    (e.activity_type || '').toLowerCase().includes(term)
                                ).length;
                                const totalMinutes = employeeEntries.filter(e => 
                                    (e.description || '').toLowerCase().includes(term) ||
                                    (e.customer_name || '').toLowerCase().includes(term) ||
                                    (e.activity_type || '').toLowerCase().includes(term)
                                ).reduce((sum, e) => sum + (e.duration_minutes || 0), 0);
                                return (
                                    <p className="text-xs text-muted-foreground mt-2 px-1">
                                        {filteredCount} von {employeeEntries.length} Einträgen • {Math.floor(totalMinutes / 60)}h {totalMinutes % 60}m gefiltert
                                    </p>
                                );
                            })()}
                        </div>
                        {loadingEntries ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                            </div>
                        ) : (
                            <div className="space-y-3 mt-4">
                                {employeeEntries.length === 0 && (
                                    <p className="text-center text-muted-foreground py-8">Keine Einträge im ausgewählten Zeitraum</p>
                                )}
                                {(() => {
                                    const term = entrySearch.toLowerCase().trim();
                                    const filtered = term
                                        ? employeeEntries.filter(e =>
                                            (e.description || '').toLowerCase().includes(term) ||
                                            (e.customer_name || '').toLowerCase().includes(term) ||
                                            (e.activity_type || '').toLowerCase().includes(term)
                                          )
                                        : employeeEntries;
                                    if (employeeEntries.length > 0 && filtered.length === 0) {
                                        return (
                                            <p className="text-center text-muted-foreground py-8" data-testid="entry-search-empty">
                                                Keine Einträge passen zu "{entrySearch}"
                                            </p>
                                        );
                                    }
                                    return filtered.map((entry) => (
                                    <div key={entry.id} className="flex items-start gap-3 p-3 rounded-xl bg-muted/30 border border-border/50">
                                        {editingEntry?.id === entry.id ? (
                                            // Edit Form
                                            <div className="flex-1 space-y-3">
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div>
                                                        <Label className="text-xs">Datum</Label>
                                                        <Input
                                                            type="date"
                                                            value={editForm.date}
                                                            onChange={(e) => setEditForm({...editForm, date: e.target.value})}
                                                            className="mt-1"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label className="text-xs">Dauer (Minuten)</Label>
                                                        <Input
                                                            type="number"
                                                            value={editForm.duration_minutes}
                                                            onChange={(e) => setEditForm({...editForm, duration_minutes: parseInt(e.target.value)})}
                                                            className="mt-1"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div>
                                                        <Label className="text-xs">Aktivität</Label>
                                                        <Input
                                                            value={editForm.activity_type}
                                                            onChange={(e) => setEditForm({...editForm, activity_type: e.target.value})}
                                                            className="mt-1"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label className="text-xs">Kunde</Label>
                                                        <Select 
                                                            value={editForm.customer_id || 'none'} 
                                                            onValueChange={(v) => setEditForm({...editForm, customer_id: v === 'none' ? '' : v})}
                                                        >
                                                            <SelectTrigger className="mt-1">
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="none">Kein Kunde</SelectItem>
                                                                {customers.map(c => (
                                                                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                </div>
                                                <div>
                                                    <Label className="text-xs">Beschreibung</Label>
                                                    <Textarea
                                                        value={editForm.description}
                                                        onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                                                        className="mt-1"
                                                        rows={2}
                                                    />
                                                </div>
                                                <div className="flex gap-2 justify-end">
                                                    <Button variant="ghost" size="sm" onClick={() => setEditingEntry(null)}>
                                                        Abbrechen
                                                    </Button>
                                                    <Button size="sm" onClick={handleSaveEntry}>
                                                        <Save className="w-4 h-4 mr-1" />
                                                        Speichern
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            // Display Entry
                                            <>
                                                <div className="w-12 h-12 rounded-lg bg-primary/10 flex flex-col items-center justify-center shrink-0">
                                                    <span className="text-xs font-bold text-primary">
                                                        {entry.date ? format(parseISO(entry.date), 'dd', { locale: de }) : '--'}
                                                    </span>
                                                    <span className="text-[10px] text-muted-foreground">
                                                        {entry.date ? format(parseISO(entry.date), 'MMM', { locale: de }) : '--'}
                                                    </span>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-medium text-sm">
                                                            {entry.duration_minutes ? `${Math.floor(entry.duration_minutes / 60)}h ${entry.duration_minutes % 60}m` : '--'}
                                                        </span>
                                                        {entry.activity_type && (
                                                            <Badge variant="secondary" className="text-xs">{entry.activity_type}</Badge>
                                                        )}
                                                        {entry.is_vacation && (
                                                            <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">Urlaub</Badge>
                                                        )}
                                                    </div>
                                                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                                                        {entry.customer_name || 'Kein Kunde'} • {entry.description || 'Keine Beschreibung'}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <Button 
                                                        variant="ghost" 
                                                        size="icon"
                                                        className="h-8 w-8"
                                                        onClick={() => startEditEntry(entry)}
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </Button>
                                                    <Button 
                                                        variant="ghost" 
                                                        size="icon"
                                                        className="h-8 w-8 text-destructive hover:text-destructive"
                                                        onClick={() => handleDeleteEntry(entry.id)}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ));
                                })()}
                            </div>
                        )}
                    </ScrollArea>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminTimeStats;
